import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { RequiredStar } from 'components/RequiredStarSpan'
import { FunctionComponent, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BankDetails } from 'types/bankDetails'
import { useCreateBankAccountMutation } from 'services/bankAccounts'
import { v4 as uuidv4 } from 'uuid'
import { zodResolver } from '@hookform/resolvers/zod'
import { createBankAccountFormSchema } from '../validations/bank-accounts-form-validation'

interface Props {
  handleClose: () => void
  data?: BankDetails | null
  organizationId: string
}

export const BankDetailsForm: FunctionComponent<Props> = ({
  handleClose,
  data,
  organizationId,
}) => {
  const { t } = useTranslation()
  const [
    createBankAccount,
    { isLoading: isCreateAccountLoading, isSuccess: isCreateAccountSuccess },
  ] = useCreateBankAccountMutation()
  const isEditing = !!data
  const schema = createBankAccountFormSchema(t)

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<Partial<BankDetails>>({
    defaultValues: {
      name: data ? data.name : '',
      accountNumber: data ? data.accountNumber : '',
      accountCurrency: data ? data.accountCurrency : '',
      accountHolderName: data ? data.accountHolderName : '',
      beneficiaryBankName: data ? data.beneficiaryBankName : '',
      beneficiaryBankAddress: data ? data.beneficiaryBankAddress : '',
      taxId: data ? data.taxId : '',
      correspondentSwiftBicCode: data ? data.correspondentSwiftBicCode : '',
      description: data ? data.description : '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isCreateAccountSuccess) {
      handleClose()
      reset()
    }
  }, [handleClose, isCreateAccountSuccess, reset])

  const onSubmit = (details: Partial<BankDetails>) => {
    if (isEditing) {
      createBankAccount({ ...data, ...details, organizationId })
    } else {
      createBankAccount({ ...details, id: uuidv4(), organizationId })
    }
  }

  return (
    <BootstrapDialog open={true} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('bank-details')}
      </DialogTitle>

      <DialogCloseButton handleClose={handleClose} />

      <DialogContent dividers>
        <form id="new-bank-details-form" onSubmit={handleSubmit(onSubmit)}>
          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('accountNumber')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.accountNumber}
              {...register('accountNumber')}
            />

            {errors.accountNumber && (
              <FormHelperText error>{errors.accountNumber?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('bankName')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.name}
              {...register('name')}
            />

            {errors.name && (
              <FormHelperText error>{errors.name?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('accountCurrency')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.accountCurrency}
              {...register('accountCurrency')}
            />

            {errors.accountCurrency && (
              <FormHelperText error>
                {errors.accountCurrency?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('accountHolderName')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.accountHolderName}
              {...register('accountHolderName')}
            />

            {errors.accountHolderName && (
              <FormHelperText error>
                {errors.accountHolderName?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('swift')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.correspondentSwiftBicCode}
              {...register('correspondentSwiftBicCode')}
            />

            {errors.correspondentSwiftBicCode && (
              <FormHelperText error>
                {errors.correspondentSwiftBicCode?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('beneficiaryBankName')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.beneficiaryBankName}
              {...register('beneficiaryBankName')}
            />

            {errors.beneficiaryBankName && (
              <FormHelperText error>
                {errors.beneficiaryBankName?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('beneficiaryBankAddress')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.beneficiaryBankAddress}
              {...register('beneficiaryBankAddress')}
            />

            {errors.beneficiaryBankAddress && (
              <FormHelperText error>
                {errors.beneficiaryBankAddress?.message}
              </FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('tin')} <RequiredStar>*</RequiredStar>
                </>
              }
              variant="outlined"
              error={!!errors.taxId}
              {...register('taxId')}
            />
            {errors.taxId && (
              <FormHelperText error>{errors.taxId?.message}</FormHelperText>
            )}
          </FormControl>

          <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
            <TextField
              label={
                <>
                  {t('description')} <RequiredStar>*</RequiredStar>
                </>
              }
              fullWidth
              multiline
              minRows={4}
              variant="outlined"
              {...register('description')}
            />
            {errors.description && (
              <FormHelperText error>{errors.description?.message}</FormHelperText>
            )}
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          autoFocus
          type="submit"
          form="new-bank-details-form"
          sx={{ mr: 1 }}
          disabled={isCreateAccountLoading}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </BootstrapDialog>
  )
}
